import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, CheckCircle2, Package, TriangleAlert, Play, Loader2, Printer, Share2, FileDown, Users, Check, Clapperboard, Wand2, X, RotateCcw, Sparkles, GraduationCap, ArrowUp, ArrowDown, Trash2, Plus, ImagePlus } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { NarrationMeter, isOverNarration } from "@/components/NarrationMeter";
import { useAuth } from "@/context/AuthContext";
import { storeForCategory } from "@/lib/shop";
import PrintableManual from "@/components/PrintableManual";
import { shareManual, printManual, savePdf } from "@/lib/share";
import { toast } from "sonner";

import { RecipeCard } from "@/components/RecipeCard";
const COMMUNITY_CATS = ["Lifestyle", "Education (Kids)", "Education (Teens)", "Education (Adults)", "Teachers", "Family", "Food", "Crypto", "Finance", "Government", "Current News", "Philosophy", "Religion", "History", "Tech", "Home", "Gardening", "People", "Fashion", "Animals", "Sports", "Games", "Travel", "Jobs", "Music", "Automotive", "Video Games", "Entertainment", "Health", "Self Help", "Fitness", "Collectibles", "Hobbies", "Weather", "Art"];
const SKILL_LABELS = { beginner: "Beginner", handy: "Handy", pro: "Pro" };

const InsertHere = ({ index, onClick, disabled }) => (
  <div className="flex items-center justify-center py-1">
    <button
      data-testid={`diy-insert-here-${index}`}
      onClick={onClick}
      disabled={disabled}
      className="group inline-flex items-center gap-1.5 text-[11px] font-bold text-[#5d8a52] hover:text-[#8fbf82] disabled:opacity-30 transition-colors"
    >
      <span className="w-6 h-6 rounded-full border-2 border-dashed border-current flex items-center justify-center"><Plus size={13} /></span>
      Insert step here
    </button>
  </div>
);


// Resize an uploaded image to a data URL before upload (keeps files small for the proxy).
function resizeToDataUrl(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const r = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

export default function ManualOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [manual, setManual] = useState(null);
  const [err, setErr] = useState(false);
  const [shared, setShared] = useState(false);
  const [shareCat, setShareCat] = useState("Lifestyle");
  const [shareBusy, setShareBusy] = useState(false);
  const [makingVideo, setMakingVideo] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [showHeroFix, setShowHeroFix] = useState(false);
  const [heroFixText, setHeroFixText] = useState("");
  const [heroZoom, setHeroZoom] = useState("");
  const [heroBusy, setHeroBusy] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const [stepBusy, setStepBusy] = useState(false);
  const [insAt, setInsAt] = useState(null);   // insert AFTER this index (null = closed)
  const [insImg, setInsImg] = useState(null);
  const [insText, setInsText] = useState("");
  const [insBusy, setInsBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/manuals/${id}`);
      setManual(data);
      setShared(!!data.shared);
      setShareCat(data.share_category || data.category || "Lifestyle");
    } catch { setErr(true); }
  };

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line

  // ── DIY step manager (reorder / delete / insert anywhere) ──
  const moveStep = async (from, to) => {
    if (stepBusy || to < 0 || to >= (manual.steps?.length || 0)) return;
    setStepBusy(true);
    try { await api.post(`/manuals/${id}/steps/reorder`, { from_index: from, to_index: to }); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reorder"); }
    finally { setStepBusy(false); }
  };
  const deleteStep = async (i) => {
    if (stepBusy) return;
    if (!window.confirm(`Delete Step ${i + 1}? This can't be undone.`)) return;
    setStepBusy(true);
    try { await api.delete(`/manuals/${id}/steps/${i}`); await refresh(); toast.success("Step deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
    finally { setStepBusy(false); }
  };
  const onPickInsertImg = async (file) => {
    if (!file) return;
    try { setInsImg(await resizeToDataUrl(file)); } catch { toast.error("Couldn't read that image"); }
  };
  const saveInsertAt = async () => {
    if (!insImg) { toast.error("Add a photo for the new step"); return; }
    if (!insText.trim()) { toast.error("Describe the new step in your own words"); return; }
    if (isOverNarration(insText)) { toast.error("Keep it under ~42 words (about 16 seconds)"); return; }
    setInsBusy(true);
    try {
      await api.post(`/manuals/${id}/steps/insert`, { after_index: insAt, text: insText.trim(), image: insImg });
      toast.success("New step inserted 🎬");
      setInsAt(null); setInsImg(null); setInsText("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't insert the step"); }
    finally { setInsBusy(false); }
  };

  // Jump straight to the "All done" page where the video is rendered (does NOT render here).
  const makeVideoNow = async () => {
    setMakingVideo(true);
    try { await api.post(`/manuals/${id}/complete`); } catch { /* still go */ }
    navigate(`/manual/${id}/complete`);
  };

  // Digital Assets Only — build the teaching kit (photos + printable/presentation assets), no video.
  const makeAssetsOnly = () => navigate(`/manual/${id}/kit`);

  // Hero-as-template: set BEFORE step photos exist so they're generated matching the hero once.
  const toggleTemplate = async () => {
    const next = !manual.use_hero_template;
    setTplBusy(true);
    try {
      await api.post(`/manuals/${id}/hero-template`, { enabled: next });
      setManual((m) => ({ ...m, use_hero_template: next }));
      toast.success(next ? "Every step photo will now match this image 🎨" : "Template turned off");
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setTplBusy(false);
    }
  };

  const regenerateHero = async () => {
    if ((!heroFixText.trim() && !heroZoom) || heroBusy) return;
    setHeroBusy(true);
    try {
      const { data } = await api.post(`/manuals/${id}/hero/regenerate`, { instruction: heroFixText.trim() || null, zoom: heroZoom || null });
      setHeroLoaded(false);
      setManual((m) => ({
        ...m,
        hero_image_id: data.image_id,
        original_hero_image_id: data.original_hero_image_id,
        redo_hero_image_id: data.redo_hero_image_id,
        hero_redo_used: true,
      }));
      setHeroFixText("");
      setHeroZoom("");
      setShowHeroFix(false);
      toast.success("Hero image updated ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't edit the image — try again.");
    } finally {
      setHeroBusy(false);
    }
  };

  const toggleHero = async (use) => {
    try {
      const { data } = await api.post(`/manuals/${id}/hero/toggle`, { use });
      setHeroLoaded(false);
      setManual((m) => ({ ...m, hero_image_id: data.image_id }));
    } catch {
      toast.error("Couldn't switch — try again.");
    }
  };

  const saveShare = async (nextShared, nextCat) => {    setShareBusy(true);
    try {
      await api.post(`/manuals/${id}/share`, { shared: nextShared, category: nextCat });
      setShared(nextShared);
      setShareCat(nextCat);
      toast.success(nextShared ? "Posted to Community 🎉" : "Removed from Community");
    } catch {
      toast.error("Couldn't update sharing");
    } finally {
      setShareBusy(false);
    }
  };

  if (err) return <Centered text="Manual not found." action={() => navigate("/")} />;
  if (!manual) return <Centered loading />;

  const shopEntry = storeForCategory(user?.settings, manual.category);

  return (
    <div className="max-w-2xl mx-auto px-4 pb-10 text-white">
      <div className="flex items-center justify-between py-4">
        <button
          data-testid="back-to-home-btn"
          onClick={() => navigate("/studio")}
          className="flex items-center gap-2 text-white/70 font-semibold hover:text-white transition-colors"
        >
          <ArrowLeft size={18} /> New manual
        </button>
        <div className="flex items-center gap-1">
          <button
            data-testid="pdf-manual-btn"
            onClick={() => savePdf(manual)}
            title="Save as PDF"
            className="flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-[#8fbf82] hover:bg-[#5d8a52]/15 px-3 py-2 rounded-full transition-colors"
          >
            <FileDown size={17} /> <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            data-testid="print-manual-btn"
            onClick={printManual}
            title="Print"
            className="flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-[#8fbf82] hover:bg-[#5d8a52]/15 px-3 py-2 rounded-full transition-colors"
          >
            <Printer size={17} /> <span className="hidden sm:inline">Print</span>
          </button>
          <button
            data-testid="share-manual-btn"
            onClick={() => shareManual(manual)}
            title="Share"
            className="flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-[#8fbf82] hover:bg-[#5d8a52]/15 px-3 py-2 rounded-full transition-colors"
          >
            <Share2 size={17} /> <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      </div>

      <div className="sms-tweed rounded-2xl overflow-hidden border-2 border-[#5d8a52] bg-[#0f0f16] sms-fade-up" style={{ boxShadow: "0 24px 70px -24px rgba(0,0,0,.7), 0 0 34px -10px rgba(93,138,82,.3)" }}>
        {/* Hero — 16:9 landscape on top (matches the All Done page so the crop is always visible) */}
        <div className="relative w-full aspect-video overflow-hidden bg-black" data-testid="hero-frame">
          {manual.hero_image_id ? (
            <img src={imageUrl(manual.hero_image_id)} alt={manual.title} onLoad={() => setHeroLoaded(true)} className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/35"><Package size={44} /></div>
          )}
          {manual.hero_image_id && !heroLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <span className="italic text-[11px] text-white/85" data-testid="hero-loading-hint">Please wait while image loads…</span>
            </div>
          )}
        </div>

        {/* Fix this Photo — inline, right under the hero (same AI edit flow as the gallery) */}
        {manual.hero_image_id && !(manual.moviestar && !manual.moviestar_step_photos) && !manual.hero_redo_used && (
          <div className="px-6 pt-5" data-testid="hero-fix-inline">
            <p className="text-[11px] text-white/55 mb-1.5 leading-snug">
              Enter what you want to fix in the image — be specific about what you'd like to see.
            </p>
            <textarea
              data-testid="hero-fix-input"
              value={heroFixText}
              onChange={(e) => setHeroFixText(e.target.value)}
              rows={3}
              maxLength={900}
              placeholder="Describe the changes you'd like to make to this image — be specific"
              disabled={heroBusy}
              className="w-full rounded-xl px-4 py-3 text-sm text-white bg-[#14121c] placeholder:text-white/40 outline-none disabled:opacity-60 resize-none"
              style={{ border: "2px solid #5d8a52" }}
            />
            <div className="mt-2">
              <label className="text-[11px] font-bold text-white/60 uppercase tracking-wide">Zoom image</label>
              <select
                data-testid="hero-fix-zoom"
                value={heroZoom}
                onChange={(e) => setHeroZoom(e.target.value)}
                className="w-full mt-1 rounded-xl px-3 py-2.5 text-sm font-bold text-white bg-[#14121c] outline-none"
                style={{ border: "2px solid #5d8a52" }}
              >
                <option value="">Keep current framing</option>
                <option value="tight">Tight shot — full bleed (emblems, engines, products)</option>
                <option value="70">Zoom in · 70%</option>
                <option value="60">Zoom in · 60%</option>
                <option value="50">Zoom in · 50%</option>
                <option value="40">Zoom in · 40%</option>
                <option value="wide">Wide shot — pull back (drone / establishing)</option>
              </select>
            </div>
            <button
              data-testid="hero-fix-submit"
              onClick={regenerateHero}
              disabled={heroBusy || (!heroFixText.trim() && !heroZoom)}
              className="mt-2.5 w-full font-extrabold text-sm py-3 rounded-xl flex items-center justify-center gap-2 text-white transition-colors disabled:opacity-50"
              style={{ background: "#1e3aaa" }}
            >
              {heroBusy ? <><Loader2 size={16} className="spin" /> Applying your edit…</> : <><Wand2 size={16} /> Fix this Photo</>}
            </button>
            <p className="text-[11px] text-white/45 mt-2 text-center">One edit per cover · <b className="text-[#8fbf82]">$0.25</b> · your original is always kept.</p>
          </div>
        )}

        <div className="px-6 pb-6 pt-6">
          {/* Badges — centered under the image */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#8fbf82] bg-[#5d8a52]/15 border border-[#5d8a52] px-3 py-1.5 rounded-full">
              <CheckCircle2 size={15} /> Steps Ready
            </span>
            {manual.skill_level && SKILL_LABELS[manual.skill_level] && (
              <span
                data-testid="manual-skill-badge"
                className="inline-flex items-center gap-1.5 text-sm font-bold px-3.5 py-1.5 rounded-full"
                style={{ background: "#4f7746", color: "#fff" }}
              >
                {SKILL_LABELS[manual.skill_level]}
              </span>
            )}
          </div>

          {/* Title — below the image, centered */}
          <h1 className="text-2xl md:text-3xl font-extrabold mt-3 leading-tight text-center text-white" data-testid="manual-title">
            {manual.title}
          </h1>

          {/* Meta — centered */}
          <div className="flex items-center justify-center gap-3 text-white/60 mt-3 font-medium">
            <span className="flex items-center gap-1.5"><Clock size={17} className="text-[#8fbf82]" /> {manual.estimated_time}</span>
            <span className="text-white/25">•</span>
            <span>{manual.steps.length} steps</span>
          </div>


          {/* Hero image controls — edit the cover & lock it as the template BEFORE step photos are made */}
          {manual.hero_image_id && !(manual.moviestar && !manual.moviestar_step_photos) && (
            <div className="mt-5 rounded-2xl border-2 border-[#5d8a52] p-4 space-y-3" data-testid="hero-controls">
              {manual.hero_redo_used && manual.original_hero_image_id && manual.redo_hero_image_id && (
                <div className="flex flex-col items-center gap-2" data-testid="hero-version-toggle-wrap">
                  <span className="text-[11px] font-bold text-white/60 uppercase tracking-wide">Cover version</span>
                  <div className="flex rounded-xl overflow-hidden border-2 border-[#5d8a52]" data-testid="hero-version-toggle">
                    {["original", "redo"].map((v) => {
                      const active = v === "original"
                        ? manual.hero_image_id === manual.original_hero_image_id
                        : manual.hero_image_id === manual.redo_hero_image_id;
                      return (
                        <button
                          key={v}
                          data-testid={`hero-use-${v}`}
                          onClick={() => toggleHero(v)}
                          className="px-4 py-2 text-xs font-extrabold transition-colors"
                          style={active ? { background: "#1e3aaa", color: "#fff" } : { background: "#14241b", color: "#8fbf82" }}
                        >
                          {v === "original" ? "Original" : "New"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-sm flex items-center gap-2 text-white">
                    <Sparkles size={16} className="text-[#5d8a52]" /> Use this image as a template
                  </div>
                  <p className="text-xs text-white/60 mt-0.5">Every step photo will be created to match this cover's subject, style &amp; colour — set once, no re-do fees.</p>
                </div>
                <button
                  data-testid="hero-template-toggle"
                  role="switch"
                  aria-checked={!!manual.use_hero_template}
                  onClick={toggleTemplate}
                  disabled={tplBusy}
                  className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1 disabled:opacity-60"
                  style={{ background: manual.use_hero_template ? "#5d8a52" : "#2a2a33" }}
                >
                  <span className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all" style={{ left: manual.use_hero_template ? 28 : 4 }} />
                </button>
              </div>
            </div>
          )}

          {/* STEP FOUR header — full-width blue stripe band above the Make Video Now button */}
          <div className="-mx-6 mt-5 mb-3 py-2.5 text-center" style={{ background: "#1e3aaa" }} data-testid="step-two-title-wrap">
            <span data-testid="step-two-title" className="font-extrabold text-xl tracking-wide text-white">STEP FOUR</span>
          </div>

          {/* Step One — decide what to do next */}
          <div className="mt-3 space-y-3" data-testid="step-one-actions">
            {manual.assets_credited && !manual.video_credited ? (
              <button
                data-testid="open-teaching-kit-btn"
                onClick={makeAssetsOnly}
                className="w-full text-white font-extrabold text-lg py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors"
                style={{ background: "#dc2626" }}
              >
                <GraduationCap size={20} /> Open your Teaching Kit
              </button>
            ) : (
              <>
                <button
                  data-testid="make-video-now-btn"
                  onClick={makeVideoNow}
                  disabled={makingVideo}
                  className="w-full text-white font-extrabold text-lg py-3.5 rounded-2xl flex flex-col items-center justify-center gap-0.5 transition-colors disabled:opacity-60"
                  style={{ background: "#dc2626" }}
                >
                  <span className="flex items-center gap-2">
                    {makingVideo ? <Loader2 size={20} className="spin" /> : <Clapperboard size={20} />} Make Video Now <span className="font-bold opacity-90">($3.99)</span>
                  </span>
                  <span className="text-[11px] font-semibold text-white/85 tracking-wide">Includes Digital Asset Kit</span>
                </button>
                <button
                  data-testid="make-assets-only-btn"
                  onClick={makeAssetsOnly}
                  className="w-full text-white font-extrabold text-lg py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors"
                  style={{ background: "#b91c1c" }}
                >
                  <GraduationCap size={20} /> Make Digital Assets Only — No Video <span className="font-bold opacity-90">($2.99)</span>
                </button>
                <p className="text-xs text-white/60 leading-relaxed px-1" data-testid="assets-only-pitch">
                  Perfect for <span className="font-semibold text-[#8fbf82]">educators &amp; trainers</span> — get the entire lesson kit in one download:
                  all 15 step images, a printable PDF guide, ready-to-present Keynote/PowerPoint slides, a student worksheet, an answer key, and a quiz.
                  No video, just <span className="font-semibold text-white">presentation and study-ready materials</span>.
                </p>
              </>
            )}
            <button
              data-testid="start-manual-btn"
              onClick={() => navigate(`/manual/${id}/step/0`)}
              className="w-full font-bold text-base py-3.5 rounded-2xl flex items-center justify-center gap-2 border-2 border-[#5d8a52] text-[#8fbf82] bg-transparent hover:bg-[#5d8a52]/15 transition-colors"
            >
              <Play size={18} fill="#4f7746" /> Start Step 1
            </button>
          </div>

          {manual.recipe && (
            <div className="mt-6">
              <RecipeCard recipe={manual.recipe} />
            </div>
          )}

          {/* DIY step manager — reorder, delete, and insert steps anywhere (DIY only) */}
          {manual.diy && manual.steps?.length > 0 && (
            <div className="sms-tweed mt-6 rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-4" data-testid="diy-step-manager">
              <div className="flex items-center gap-2 text-white/70 font-bold tracking-wide text-sm uppercase mb-1">
                <Wand2 size={16} className="text-[#8fbf82]" /> Edit Steps
              </div>
              <p className="text-xs text-white/45 mb-3">Reorder, remove, or insert a brand-new step anywhere — up to 15.</p>
              <InsertHere index={-1} onClick={() => { setInsImg(null); setInsText(""); setInsAt(-1); }} disabled={stepBusy || manual.steps.length >= 15} />
              {manual.steps.map((s, i) => (
                <div key={i}>
                  <div className="flex items-center gap-3 rounded-xl border border-[#2a3a26] bg-black/40 p-2" data-testid={`diy-step-row-${i}`}>
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-black shrink-0 border border-[#2a3a26]">
                      {s.thumbnail_id ? <img src={imageUrl(s.thumbnail_id)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImagePlus size={16} /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-extrabold text-[#8fbf82]">STEP {i + 1}</div>
                      <div className="text-sm text-white/85 leading-snug line-clamp-2">{s.title || s.narration}</div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button data-testid={`diy-step-up-${i}`} onClick={() => moveStep(i, i - 1)} disabled={stepBusy || i === 0} className="w-7 h-7 rounded-md bg-[#14241b] text-[#8fbf82] flex items-center justify-center disabled:opacity-30 hover:bg-[#1b3322] transition-colors"><ArrowUp size={14} /></button>
                      <button data-testid={`diy-step-down-${i}`} onClick={() => moveStep(i, i + 1)} disabled={stepBusy || i === manual.steps.length - 1} className="w-7 h-7 rounded-md bg-[#14241b] text-[#8fbf82] flex items-center justify-center disabled:opacity-30 hover:bg-[#1b3322] transition-colors"><ArrowDown size={14} /></button>
                    </div>
                    <button data-testid={`diy-step-delete-${i}`} onClick={() => deleteStep(i)} disabled={stepBusy || manual.steps.length <= 1} className="w-8 h-8 rounded-md bg-[#2a1414] text-[#e0455c] flex items-center justify-center shrink-0 disabled:opacity-30 hover:bg-[#3a1a1a] transition-colors"><Trash2 size={15} /></button>
                  </div>
                  <InsertHere index={i} onClick={() => { setInsImg(null); setInsText(""); setInsAt(i); }} disabled={stepBusy || manual.steps.length >= 15} />
                </div>
              ))}
              {manual.steps.length >= 15 && <p className="text-[11px] text-[#e0a92e] mt-1 text-center font-semibold">Maximum of 15 steps reached.</p>}
            </div>
          )}

          {manual.supplies?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 text-white/70 font-bold tracking-wide text-sm uppercase">
                <Package size={17} className="text-[#5d8a52]" /> Required Supplies
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-white/80 leading-relaxed" data-testid="supplies-list">
                {manual.supplies.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#a9c39c] shrink-0" />
                    <span>{s}</span>
                  </span>
                ))}
              </div>
              {shopEntry.enabled && shopEntry.store === "Amazon" && (
                <p className="text-xs text-white/40 mt-3" data-testid="amazon-disclosure-overview">
                  As an Amazon Associate, Vittyyo earns from qualifying purchases.
                </p>
              )}
            </div>
          )}

          {manual.safety_warnings?.length > 0 && (
            <div className="mt-7 rounded-2xl p-5" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="safety-panel">
              <div className="flex items-center gap-2 text-[#e0a92e] font-bold tracking-wide text-sm uppercase">
                <span aria-hidden="true">⚠️</span> Safety Warnings <span aria-hidden="true">⚠️</span>
              </div>
              <ul className="mt-3 space-y-2">
                {manual.safety_warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/85">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e0a92e] mt-2.5 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Share to Community */}
          <div className="mt-7 rounded-2xl border-2 border-[#5d8a52] p-5" data-testid="share-community-section">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-bold flex items-center gap-2"><Users size={17} className="text-[#5d8a52]" /> Share to Community</div>
                <p className="text-sm text-white/60 mt-1">Let anyone discover, view & download this manual.</p>
              </div>
              <button
                data-testid="share-community-toggle"
                role="switch"
                aria-checked={shared}
                onClick={() => saveShare(!shared, shareCat)}
                disabled={shareBusy}
                className="relative w-14 h-8 rounded-full transition-colors shrink-0 mt-1 disabled:opacity-60"
                style={{ background: shared ? "#5d8a52" : "#2a2a33" }}
              >
                <span className="absolute top-1 w-6 h-6 bg-white rounded-full transition-all" style={{ left: shared ? 28 : 4 }} />
              </button>
            </div>
            {shared && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-white/45 mb-2">Category</div>
                <div className="flex flex-wrap gap-2">
                  {COMMUNITY_CATS.map((c) => {
                    const active = shareCat === c;
                    return (
                      <button
                        key={c}
                        data-testid={`share-cat-${c}`}
                        onClick={() => { if (c !== shareCat) saveShare(true, c); }}
                        disabled={shareBusy}
                        className="px-3 py-1.5 rounded-full text-sm font-bold transition-colors flex items-center gap-1"
                        style={active ? { background: "#4f7746", color: "#fff" } : { background: "#14241b", color: "#8fbf82" }}
                      >
                        {active && <Check size={13} />} {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PrintableManual manual={manual} />

      {/* Hero edit lightbox — same "fix a photo" flow as the All-Done gallery */}
      {showHeroFix && manual.hero_image_id && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          data-testid="hero-fix-modal"
          onClick={() => !heroBusy && setShowHeroFix(false)}
        >
          <div
            className="sms-tweed bg-[#0f0f16] border-2 border-[#5d8a52] rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-black aspect-video">
              <img src={imageUrl(manual.hero_image_id)} alt="Hero" className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
              {heroBusy && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 backdrop-blur-sm" data-testid="hero-fix-busy">
                  <Loader2 size={26} className="spin text-[#8fbf82]" />
                  <span className="text-xs font-semibold text-[#8fbf82]">Applying your edit…</span>
                </div>
              )}
              <button
                data-testid="hero-fix-close"
                onClick={() => !heroBusy && setShowHeroFix(false)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5">
              {manual.hero_redo_used ? (
                <div className="text-center space-y-2" data-testid="hero-fix-done">
                  <p className="text-sm text-white font-bold">You've used your one hero edit.</p>
                  <p className="text-xs text-white/60">Switch freely between the <b>Original</b> and your <b>New</b> version using the toggle below the cover — it's free.</p>
                  <button
                    onClick={() => setShowHeroFix(false)}
                    className="mt-2 w-full font-bold text-sm py-3 rounded-xl bg-[#171426] text-white hover:bg-[#2c2941] transition-colors"
                  >
                    Got it
                  </button>
                </div>
              ) : (
                <>
                  <label className="text-[11px] font-bold text-white/60 uppercase tracking-wide">Tell us what to change</label>
                  <p className="text-[11px] text-white/45 mt-1 mb-1 leading-snug">Be specific about what you'd like to see.</p>
                  <textarea
                    data-testid="hero-fix-input"
                    value={heroFixText}
                    onChange={(e) => setHeroFixText(e.target.value)}
                    rows={3}
                    maxLength={900}
                    placeholder='e.g. "make the Porsche red, lower the camera close to the ground"'
                    className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none resize-none"
                    style={{ border: "2px solid #5d8a52" }}
                    autoFocus
                  />
                  <div className="mt-2">
                    <label className="text-[11px] font-bold text-white/60 uppercase tracking-wide">Zoom image</label>
                    <select
                      data-testid="hero-fix-zoom"
                      value={heroZoom}
                      onChange={(e) => setHeroZoom(e.target.value)}
                      className="w-full mt-1 rounded-xl px-3 py-2.5 text-sm font-bold text-white bg-[#14121c] outline-none"
                      style={{ border: "2px solid #5d8a52" }}
                    >
                      <option value="">Keep current framing</option>
                      <option value="tight">Tight shot — full bleed (emblems, engines, products)</option>
                      <option value="70">Zoom in · 70%</option>
                      <option value="60">Zoom in · 60%</option>
                      <option value="50">Zoom in · 50%</option>
                      <option value="40">Zoom in · 40%</option>
                      <option value="wide">Wide shot — pull back (drone / establishing)</option>
                    </select>
                  </div>
                  <p className="text-[11px] text-white/45 mt-2">One edit per hero · <b className="text-[#8fbf82]">$0.25</b> is deducted from your balance. Your original is always kept.</p>
                  <button
                    data-testid="hero-fix-submit"
                    onClick={regenerateHero}
                    disabled={heroBusy || (!heroFixText.trim() && !heroZoom)}
                    className="mt-3 w-full font-extrabold text-sm py-3 rounded-xl flex items-center justify-center gap-2 text-white transition-colors disabled:opacity-50"
                    style={{ background: "#1e3aaa" }}
                  >
                    {heroBusy ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />} Make the edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {insAt !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => !insBusy && setInsAt(null)} data-testid="diy-insert-overlay">
          <div onClick={(e) => e.stopPropagation()} className="sms-tweed w-full max-w-lg rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] text-white p-5 sms-fade-up" style={{ boxShadow: "0 30px 90px -24px rgba(0,0,0,.85), 0 0 44px -14px rgba(93,138,82,.45)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-extrabold text-lg flex items-center gap-2"><Plus size={18} className="text-[#8fbf82]" /> {insAt < 0 ? "Insert a new first step" : `Insert a new step after Step ${insAt + 1}`}</div>
              <button onClick={() => !insBusy && setInsAt(null)} data-testid="diy-insert-close" className="text-white/50 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <div className="sms-tweed rounded-2xl border-2 border-[#2a3a26] bg-[#0a0a0f] p-4">
              <div className="flex gap-4">
                <label data-testid="diy-insert-photo-btn" className="w-28 h-28 shrink-0 rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex flex-col items-center justify-center cursor-pointer hover:bg-[#111] transition-colors overflow-hidden">
                  {insImg ? <img src={insImg} alt="new step" className="w-full h-full object-cover" /> : <><ImagePlus size={22} className="text-[#a9d79b]" /><span className="text-[10px] font-bold text-[#a9d79b] mt-1">Add photo</span></>}
                  <input type="file" accept="image/*" className="hidden" data-testid="diy-insert-photo-input" onChange={(e) => onPickInsertImg(e.target.files?.[0])} disabled={insBusy} />
                </label>
                <div className="flex-1 min-w-0">
                  <textarea data-testid="diy-insert-text" rows={4} value={insText} onChange={(e) => setInsText(e.target.value.slice(0, 600))} placeholder="Describe your new step in your own words…" className="w-full px-3 py-2 text-sm rounded-xl bg-black text-white placeholder:text-white/40 border-2 border-[#5d8a52] outline-none resize-none leading-snug" />
                  <NarrationMeter text={insText} className="mt-1.5" />
                </div>
              </div>
            </div>
            <button data-testid="diy-insert-save" onClick={saveInsertAt} disabled={insBusy} className="mt-4 w-full flex items-center justify-center gap-2 font-extrabold py-3 rounded-xl text-white bg-[#dc2626] hover:bg-[#b91c1c] transition-colors disabled:opacity-70">
              {insBusy ? <Loader2 size={18} className="spin" /> : <Check size={18} />} Insert Step
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ loading, text, action }) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-white/60">
      {loading ? (
        <Loader2 size={28} className="spin text-[#5d8a52]" />
      ) : (
        <>
          <p>{text}</p>
          <button onClick={action} className="sms-solid-btn text-white font-bold px-5 py-2.5 rounded-xl">
            Go home
          </button>
        </>
      )}
    </div>
  );
}
